import { getCashOutOverview } from "@payping/redux/actions";
import {
  Breadcrumb,
  Button,
  Checkbox,
  Drawer,
  Flex,
  Form,
  Modal,
  Progress,
  Space,
  Typography,
  useMessagedAsyncFn,
} from "@payping/web-components";
import React, { useState } from "react";
import { useAsync } from "react-use";

import { RequestStatusEnum, TransferTypeEnum, requestApis } from "@payping/api";
import { useAppDispatch } from "@/utils/redux";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { FormLayout } from "@/components/DashSettlement/FormLayout";
import { SubLayout } from "@/components/SubLayout";
import { Close } from "@payping/icons";
import { generatePath, useNavigate, useParams } from "react-router-dom";
import { DashboardRoutes } from "@/router";
import FirstStep from "./firstStep";
import SecondStep, { SettlementRowType } from "./secondStep";
import ThirdStep from "./thirdStep";
import DataCardStep from "./Anchor/dataCardStep";

export interface SettlementCreateFormType {
  title: string;
  description?: string;
  transferType: TransferTypeEnum;
  settlement: SettlementRowType[];
  iban: number;
  amount: number;
}

export interface SettlementConfiguration {
  title: string;
  description?: string;
  transferType: TransferTypeEnum;
  settlement: SettlementRowType[];
}

export enum CreateStepEnum {
  Configuration = 1,
  IBANList = 2,
  Verification = 3,
}
export interface OverviewType {
  withdrawableBalance: string;
  totalAmount: string;
  totalWage: string;
  total: string;
  balance: string;
}
const Create: React.FC = () => {
  const { code, step } = useParams<{ code: string; step: string }>() as { code: string; step: string };
  const stepNumber = parseInt(step, 10) as CreateStepEnum;
  const { t } = useAppTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  // const [form] = Form.useForm<SettlementCreateFormType>();
  const [formStepOne] = Form.useForm<SettlementConfiguration>();
  const [formStepTwo] = Form.useForm<SettlementRowType>();
  const [formStepThree] = Form.useForm<SettlementCreateFormType>();
  const [loadingStepOne, setLoadingStepOne] = useState(false);
  const [loadingStepTwo, setLoadingStepTwo] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  // const [modal, contextHolder] = Modal.useModal();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [requestTitle, setRequestTitle] = useState<string>("");
  const [requestDescription, setRequestDescription] = useState<string>("");
  const [transferType, setTransferType] = useState<TransferTypeEnum>(TransferTypeEnum.AUTO);
  const [requestStatus, setRequestStatus] = useState<RequestStatusEnum>(RequestStatusEnum.Incomplete);
  const [amountStepOne, setAmountStepOne] = useState(0);
  const [amountStepThree, setAmountStepThree] = useState(0);

  // const { createdRequest } = useAppSelector((state) => state.cashOuts);
  const [, setFileUploaded] = useState(false);
  const [overviewData, setOverviewData] = useState<OverviewType>({
    balance: "",
    total: "",
    totalAmount: "",
    totalWage: "",
    withdrawableBalance: "",
  });
  const nextStep = (requestCode?: string) => {
    navigate(
      generatePath(DashboardRoutes.settlement.create.url, { code: requestCode || code, step: String(stepNumber + 1) }),
    );
  };

  const prevStep = () => {
    if (stepNumber === CreateStepEnum.IBANList) {
      formStepOne.setFieldValue("title", requestTitle);
      formStepOne.setFieldValue("description", requestDescription);
      formStepOne.setFieldValue("transferType", transferType);
    }
    navigate(generatePath(DashboardRoutes.settlement.create.url, { code, step: String(stepNumber - 1) }));
  };

  const steps = [
    {
      content: (
        <FirstStep
          form={formStepOne}
          next={nextStep}
          setRequestTitle={setRequestTitle}
          setTransferType={setTransferType}
          setRequestDescription={setRequestDescription}
          setLoadingStepOne={setLoadingStepOne}
          setRequestStatus={setRequestStatus}
          setAmountStepOne={setAmountStepOne}
          transferType={transferType}
        />
      ),
      title: t("settlement.create.firstStepTitle"),
    },
    {
      content: (
        <SecondStep
          setFileUploaded={setFileUploaded}
          requestTitle={requestTitle}
          transferType={transferType}
          setRequestStatus={setRequestStatus}
          setLoadingStepTwo={setLoadingStepTwo}
          setLoadingDelete={setLoadingDelete}
          setRequestTitle={setRequestTitle}
          setRequestDescription={setRequestDescription}
          setTransferType={setTransferType}
          formStepOne={formStepOne}
          formStepTwo={formStepTwo}
          next={nextStep}
        />
      ),
      title: t("settlement.create.secondStepTitle"),
    },
    {
      content: (
        <ThirdStep
          form={formStepThree}
          setOverviewData={setOverviewData}
          setRequestTitle={setRequestTitle}
          setRequestDescription={setRequestDescription}
          setTransferType={setTransferType}
          setRequestStatus={setRequestStatus}
          setAmountStepThree={setAmountStepThree}
        />
      ),
      title: t("settlement.create.thirdStepTitle"),
    },
  ];

  const getTitle = (title: CreateStepEnum) => {
    switch (title) {
      case CreateStepEnum.Configuration:
        return t("settlement.create.configuration");
      case CreateStepEnum.IBANList:
        return t("settlement.create.ibanList");
      case CreateStepEnum.Verification:
        return t("settlement.create.verification");
      default:
        return undefined;
    }
  };

  const getContent = (content: CreateStepEnum) => {
    switch (content) {
      case CreateStepEnum.Configuration:
        return <div>{steps[0].content}</div>;
      case CreateStepEnum.IBANList:
        return <div>{steps[1].content}</div>;
      case CreateStepEnum.Verification:
        return <div>{steps[2].content}</div>;
      default:
        return undefined;
    }
  };

  useAsync(() => dispatch(getCashOutOverview()), []);

  const [{ loading: loadingDraft }, onSubmitDraft] = useMessagedAsyncFn(async () => {
    const transType = transferType || transferType;
    let requestCode: string | undefined = code;
    if (stepNumber === CreateStepEnum.Configuration) {
      const response = await requestApis.createBatchRequestWithExcelWithFileWithModelPost(
        requestTitle,
        undefined,
        requestDescription,
        undefined,
        undefined,
        transType,
      );
      requestCode = response.code;
    }
    await requestApis.changeRequestStatusWithViewModelWithCancellationTokenPut({
      code: requestCode,
      toStatus: RequestStatusEnum.Draft,
    });
  }, [code, requestTitle, requestDescription, transferType]);
  return (
    // @ts-ignore delete subNav
    <Drawer
      title={
        <Space>
          <Breadcrumb
            items={[{ title: t("settlement.create.advancedWithdrawRequest") }, { title: getTitle(stepNumber) }]}
          />
          <Progress steps={3} percent={stepNumber * 30} format={(percent) => `${(percent || 30) / 30} از 3`} />
        </Space>
      }
      closeIcon={false}
      open
      placement={"bottom"}
      height={"100%"}
      push={false}
      extra={
        <Button
          type={"text"}
          icon={<Close width={14} height={14} />}
          onClick={() => {
            if (requestTitle || requestStatus === RequestStatusEnum.Draft) {
              setIsModalOpen(true);
            } else {
              navigate(DashboardRoutes.settlement.overview.url);
            }
          }}
        ></Button>
      }
    >
      <SubLayout>
        <FormLayout
          firstChild={<div>{getContent(stepNumber)}</div>}
          secondChild={
            <DataCardStep
              requestTitle={requestTitle}
              requestDesc={requestDescription}
              transferType={transferType}
              formStepOne={formStepOne}
              formStepTwo={formStepTwo}
              formStepThree={formStepThree}
              setOpenModalCancel={setIsModalOpen}
              loadingStepOne={loadingStepOne}
              loadingStepTwo={loadingStepTwo}
              prev={prevStep}
              loadingDelete={loadingDelete}
              overviewData={overviewData}
              amountStepOne={amountStepOne}
              amountStepThree={amountStepThree}
            />
          }
        />
        {/* </Form> */}
        {/* {contextHolder} */}
      </SubLayout>
      <Modal
        maskClosable={false}
        title={t("settlement.create.cancellationOfSettlementSubmit")}
        footer={
          <Flex gap={"small"}>
            <Button type={"default"} onClick={() => setIsModalOpen(false)}>
              {t("no")}
            </Button>
            <Button
              type={"primary"}
              loading={loadingDraft}
              onClick={async () => {
                if (isChecked) {
                  await onSubmitDraft();
                  navigate(DashboardRoutes.settlement.overview.url);
                }
                navigate(DashboardRoutes.settlement.overview.url);
              }}
            >
              {t("yes")}
            </Button>
          </Flex>
        }
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
      >
        <Flex vertical={true} gap={"middle"}>
          <Typography.Text>{t("settlement.create.cancelModalDesc")}</Typography.Text>
          <Flex gap={"small"}>
            <Checkbox
              checked={isChecked}
              onChange={(e) => {
                setIsChecked(e.target.checked);
              }}
            >
              {t("settlement.create.saveDraft")}
            </Checkbox>
          </Flex>
        </Flex>
      </Modal>
    </Drawer>
  );
};

export default Create;
